import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Star, Trash2, Plus, TrendingUp, TrendingDown } from 'lucide-react';
import LoadingSpinner from '../components/LoadingSpinner';
import toast from 'react-hot-toast';

const Watchlist = () => {
  const { user, removeFromWatchlist } = useAuth();
  const [watchlistData, setWatchlistData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newTicker, setNewTicker] = useState('');

  const watchlist = user?.preferences?.watchlist || [];

  useEffect(() => {
    if (watchlist.length > 0) {
      fetchWatchlistData();
    }
  }, [watchlist]);

  const fetchWatchlistData = async () => {
    setLoading(true);
    try {
      // In a real app, you'd fetch current prices for watchlist items
      // For now, we'll simulate the data
      const mockData = watchlist.map(item => ({
        ticker: item.ticker,
        addedAt: item.addedAt,
        currentPrice: Math.random() * 200 + 50,
        dayChange: (Math.random() - 0.5) * 10,
        dayChangePercent: (Math.random() - 0.5) * 8,
        volume: Math.floor(Math.random() * 10000000) + 100000
      }));
      
      setWatchlistData(mockData);
    } catch (error) {
      console.error('Error fetching watchlist data:', error);
      toast.error('Failed to load watchlist data');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveFromWatchlist = async (ticker) => {
    const result = await removeFromWatchlist(ticker);
    if (result.success) {
      setWatchlistData(prev => prev.filter(item => item.ticker !== ticker));
    }
  };

  const handleAddTicker = async (e) => {
    e.preventDefault();
    if (!newTicker.trim()) return;

    // This would typically call addToWatchlist from context
    // For now, just show a message
    toast.success(`${newTicker.toUpperCase()} would be added to watchlist`);
    setNewTicker('');
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(price);
  };

  const formatPercent = (percent) => {
    return `${percent >= 0 ? '+' : ''}${percent.toFixed(2)}%`;
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">My Watchlist</h1>
          <p className="text-gray-600 mt-2">
            Track your favorite stocks and monitor their performance
          </p>
        </div>

        {/* Add Stock Form */}
        <form onSubmit={handleAddTicker} className="flex space-x-2">
          <input
            type="text"
            value={newTicker}
            onChange={(e) => setNewTicker(e.target.value.toUpperCase())}
            placeholder="Enter ticker (e.g., AAPL)"
            className="input w-40"
            maxLength={5}
          />
          <button
            type="submit"
            className="btn-primary flex items-center space-x-1"
          >
            <Plus className="h-4 w-4" />
            <span>Add</span>
          </button>
        </form>
      </div>

      {/* Watchlist Content */}
      {loading ? (
        <div className="flex justify-center items-center min-h-96">
          <LoadingSpinner size="lg" />
        </div>
      ) : watchlist.length === 0 ? (
        <div className="text-center py-12">
          <Star className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Your Watchlist is Empty
          </h3>
          <p className="text-gray-600 mb-6">
            Add stocks to your watchlist to track their performance and get personalized insights.
          </p>
          <Link to="/" className="btn-primary">
            Browse Recommendations
          </Link>
        </div>
      ) : (
        <div className="space-y-4">
          {watchlistData.map((stock) => (
            <div key={stock.ticker} className="card hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="flex items-center justify-center w-10 h-10 bg-primary-100 text-primary-600 rounded-full font-bold">
                    {stock.ticker.charAt(0)}
                  </div>
                  
                  <div>
                    <Link
                      to={`/stock/${stock.ticker}`}
                      className="text-xl font-bold text-gray-900 hover:text-primary-600 transition-colors"
                    >
                      {stock.ticker}
                    </Link>
                    <p className="text-sm text-gray-600">
                      Added {new Date(stock.addedAt).toLocaleDateString()}
                    </p>
                  </div>
                </div>

                <div className="flex items-center space-x-6">
                  {/* Price Info */}
                  <div className="text-right">
                    <p className="text-lg font-bold text-gray-900">
                      {formatPrice(stock.currentPrice)}
                    </p>
                    <p className={`text-sm flex items-center ${
                      stock.dayChangePercent >= 0 ? 'text-success-600' : 'text-danger-600'
                    }`}>
                      {stock.dayChangePercent >= 0 ? (
                        <TrendingUp className="h-3 w-3 mr-1" />
                      ) : (
                        <TrendingDown className="h-3 w-3 mr-1" />
                      )}
                      {formatPercent(stock.dayChangePercent)}
                    </p>
                  </div>

                  {/* Volume */}
                  <div className="text-right">
                    <p className="text-sm text-gray-600">Volume</p>
                    <p className="text-sm font-medium">
                      {stock.volume.toLocaleString()}
                    </p>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center space-x-2">
                    <Link
                      to={`/stock/${stock.ticker}`}
                      className="btn-secondary text-sm"
                    >
                      View Details
                    </Link>
                    
                    <button
                      onClick={() => handleRemoveFromWatchlist(stock.ticker)}
                      className="p-2 text-gray-400 hover:text-danger-600 transition-colors"
                      title="Remove from watchlist"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Watchlist Stats */}
      {watchlistData.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="card text-center">
            <div className="text-2xl font-bold text-gray-900">
              {watchlistData.length}
            </div>
            <div className="text-sm text-gray-600">Total Stocks</div>
          </div>
          
          <div className="card text-center">
            <div className="text-2xl font-bold text-success-600">
              {watchlistData.filter(s => s.dayChangePercent > 0).length}
            </div>
            <div className="text-sm text-gray-600">Gainers</div>
          </div>
          
          <div className="card text-center">
            <div className="text-2xl font-bold text-danger-600">
              {watchlistData.filter(s => s.dayChangePercent < 0).length}
            </div>
            <div className="text-sm text-gray-600">Losers</div>
          </div>
          
          <div className="card text-center">
            <div className="text-2xl font-bold text-gray-900">
              {watchlistData.filter(s => Math.abs(s.dayChangePercent) < 1).length}
            </div>
            <div className="text-sm text-gray-600">Stable</div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Watchlist;